import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Ascolta l'evento 'uptra:session-expired' (emesso da supabaseFetch quando la
 * sessione non è recuperabile nemmeno col refresh) e porta l'utente al login
 * con un messaggio chiaro, invece di lasciarlo su schermate vuote o falsi
 * "profilo assente".
 *
 * Anti-loop: agisce solo se c'è un utente autenticato e non siamo già sulle
 * pagine pubbliche di auth; il flag handling evita doppie esecuzioni quando
 * più fetch falliscono insieme.
 */
function SessionExpiredHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();

  const userRef = useRef(user);
  userRef.current = user;
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const handlingRef = useRef(false);

  useEffect(() => {
    const onExpired = async () => {
      if (handlingRef.current) return;
      const publicPaths = ['/', '/register', '/reset-password', '/privacy', '/termini'];
      if (!userRef.current || publicPaths.includes(pathRef.current)) return;

      handlingRef.current = true;
      // Prima la navigazione (con lo state per il messaggio), poi il signOut:
      // se signOut arrivasse prima, ProtectedRoute farebbe un suo redirect a
      // "/" SENZA state e il messaggio andrebbe perso.
      navigate('/', { replace: true, state: { sessionExpired: true } });
      try {
        await signOut(); // pulisce user/profile e lo storage di Supabase
      } catch (e) { /* best effort: siamo comunque al login */ }
      // Piccola finestra prima di riabilitare il gestore (eventi in coda)
      setTimeout(() => { handlingRef.current = false; }, 3000);
    };

    window.addEventListener('uptra:session-expired', onExpired);
    return () => window.removeEventListener('uptra:session-expired', onExpired);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

export default SessionExpiredHandler;
